import { Component, HostListener, Input, OnDestroy, OnInit, ViewEncapsulation, Inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs/Subscription';
import { GtmService } from '../../../../shared/cardholders-core/services/gtm.service';
import { UiHelperService } from '../../../../shared/cardholders-core/services/ui-helper.service';
import { ModalService } from '../../../../shared/modules/bs-modal/services/modal.service';
import { IBenefitPlusResult } from '../../models/benefit-plus-result';
import { BenefitsPlusService } from '../../services/benefits-plus.service';
import { DOCUMENT } from '@angular/common';
import { AppConfigsService } from '../../../../config/app-configs.service';

@Component({
    selector: 'app-benefits-pop-up',
    templateUrl: './benefits-pop-up.component.html',
    styleUrls: ['./benefits-pop-up.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class BenefitsPopUpComponent implements OnInit, OnDestroy {
    @Input('data') data: any;
    @Input('benefit') benefit: any;
    benefitName: string;
    categoryName: string;
    benefitResult: IBenefitPlusResult;
    isMobile: boolean;
    description: SafeHtml;
    importantInformation: SafeHtml;
    subs: Subscription[] = [];
    isPopUp: boolean = false;
    lastScrollRatio: number = 0;
    benefitsLink: string;

    constructor(
        private modalSvc: ModalService,
        public route: ActivatedRoute,
        public sanitizer: DomSanitizer,
        private benefitsSvc: BenefitsPlusService,
        private router: Router,
        private uiHelperService: UiHelperService,
        private gtmService: GtmService,
        private appConfigsService: AppConfigsService,
        @Inject(DOCUMENT) private document: Document
    ) {}

    @HostListener('window:scroll', ['$event']) // for window scroll events
    onScroll(event) {
        let pos = event.target.scrollingElement.clientHeight + event.target.scrollingElement.scrollTop;
        let max = event.target.scrollingElement.scrollHeight;
        let ratio = ((pos / max) * 100) | 0;
        if (ratio !== this.lastScrollRatio && [25, 50, 75, 100].includes(ratio)) {
            this.lastScrollRatio = ratio;
            this.gtmService.pushNewBenefitPulsePage('Benefit plus page', 'Scroll - ' + ratio);
        }
    }

    ngOnInit(): void {
        this.addRteFix();
        this.isMobile = this.uiHelperService.IsMobile();
        //todo: change to new benefits page when ready.
        this.benefitsLink = `${this.appConfigsService.appConfigs.contentRoot}/he-il/Benefits/Pages/BenfitsGallery.aspx?sourceGA=HomepageBenefitsMarketing`;
        if (this.data) {
            this.isPopUp = true;
            this.benefitName = this.data.benefitName;
            this.categoryName = this.data.categoryName;
            if (this.benefit) {
                this.setBenefit(this.benefit);
            } else {
                this.getBenefit();
            }
        } else {
            this.subs.push(
                this.route.params.subscribe((params: Params) => {
                    this.benefitName = params['name'];
                    this.categoryName = params['category'];
                    this.getBenefit();
                })
            );
        }
        if (this.uiHelperService.isBrowser && this.isPopUp) {
            document.querySelector('body').classList.add('overflow');
            document.querySelector('html').classList.add('overflow');
        }
    }

    ngOnDestroy(): void {
        this.subs.forEach((subscriber) => subscriber.unsubscribe());
        this.removeRteFix();

        if (this.uiHelperService.isBrowser && this.isPopUp) {
            document.querySelector('body').classList.remove('overflow');
            document.querySelector('html').classList.remove('overflow');
        }
    }

    getBenefit() {
        this.subs.push(
            this.benefitsSvc.getBenefit(this.benefitName, this.categoryName).subscribe((res) => {
                if (!res || !res?.result) {
                    this.router.navigate(['page-not-found']);
                }
                this.setBenefit(res.result);
            })
        );
    }

    setBenefit(benefit) {
        this.benefitResult = benefit;
        if (this.benefitResult?.description) {
            this.description = this.sanitizer.bypassSecurityTrustHtml(this.benefitResult.description);
        }
        if (this.benefitResult?.importantInformation) {
            this.importantInformation = this.sanitizer.bypassSecurityTrustHtml(this.benefitResult.importantInformation);
        }
        if (this.isPopUp) {
            this.gtmService.pushNewBenefitPulsePage('Benefit plus pop-up', this.benefitResult.name);
        } else {
            this.gtmService.pushNewBenefitPulsePage('Benefit plus page', 'Load - ' + this.benefitResult.name);
        }
        this.setAddressSeo();
    }

    navigateToBusiness() {
        window.open(this.benefitResult.businessWebsite);
    }

    closePopup(): void {
        this.modalSvc.closeModal();
    }

    addRteFix() {
        if (this.document.head.querySelector(`style[id="benefitsPlusRteStyle"]`)) {
            return;
        }
        const styleElement = this.document.head.appendChild(
            this.document.createElement('style')
        ) as HTMLStyleElement;
        styleElement.setAttribute('type', 'text/css');
        styleElement.setAttribute('id', 'benefitsPlusRteStyle');
        styleElement.innerHTML = ".benefits-plus .profile ul li {list-style-type: disc;}";
    }

    removeRteFix() {
        if (!this.document.head.querySelector(`style[id="benefitsPlusRteStyle"]`)) {
            return;
        }
        this.document.head.querySelector(`style[id="benefitsPlusRteStyle"]`).remove();
    }


    private setAddressSeo() {
        if (this.document.querySelector(`script[id="addressScript"]`)) {
            return;
        }
        let script = `
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "address": {
                     "@type": "PostalAddress",
                        "addressLocality": "${this.benefitResult.business?.location}",
                        "addressRegion": "${this.benefitResult.business?.region.name}",
                        "streetAddress": "${this.benefitResult.business?.displayAddress}"
                      },
                      "description": "${this.benefitResult.description}"
                      "name": ""${this.benefitResult.title}"",
                      "telephone": "${this.benefitResult.businessPhone}"`;
        let node = this.document.createElement('script');
        node.type = 'application/ld+json';
        node.async = true;
        node.charset = 'utf-8';
        node.text = script;
        node.id = 'addressScript';
        this.document.getElementsByTagName('head')[0].appendChild(node);
    }
}
